import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { DomainCheckResult, Money, Order, OwnedDomain } from "@websede/contracts";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { Button, Card, ErrorNotice, Select } from "../components/form.js";
import { useFeatures } from "../features.js";

function formatMoney(money: Money, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: money.currency }).format(
    money.amountMinor / 100,
  );
}

export function DomainsPage() {
  const { t, i18n } = useTranslation();
  const features = useFeatures();
  const [query, setQuery] = useState("");
  const owned = useQuery({ queryKey: ["domains", "owned"], queryFn: () => api.domains.owned() });
  const orders = useQuery({ queryKey: ["orders", "list"], queryFn: () => api.orders.list() });
  const check = useMutation({
    mutationFn: (domains: string[]) => api.domains.check({ domains }),
  });
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const domains = query
      .split(/[\s,]+/)
      .map((d) => d.trim())
      .filter(Boolean)
      .map((d) => (d.includes(".") ? d : `${d}.com`));
    if (domains.length > 0) {
      check.mutate(domains);
    }
  };

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4 md:p-8">
      <h1 className="text-[22px] text-ink">{t("nav.domains")}</h1>

      <Card title={t("domains.owned")}>
        {owned.data && owned.data.domains.length === 0 ? (
          <p className="text-sm text-ink-3">
            {features.domainSales ? t("domains.none") : t("domains.noneConnect")}
          </p>
        ) : null}
        <ul className="divide-y divide-rule">
          {(owned.data?.domains ?? []).map((d) => (
            <OwnedDomainRow key={d.id} domain={d} format={format} />
          ))}
        </ul>
      </Card>

      <ConnectCard />

      {features.domainSales ? (
        <Card title={t("domains.search")}>
          <form onSubmit={onSubmit} className="flex gap-2">
            <input
              className="flex-1 rounded-md border border-rule bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
              placeholder={t("domains.searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              disabled={check.isPending}
            >
              {check.isPending ? t("common.loading") : t("domains.search")}
            </button>
          </form>
          <p className="text-xs text-ink-3">{t("domains.priceNote")}</p>
          {check.isError ? <p className="text-sm text-danger">{t("common.errorGeneric")}</p> : null}
          {check.data ? (
            <ul className="divide-y divide-rule rounded-md border border-rule">
              {check.data.results.map((result) => (
                <DomainRow key={result.domain} result={result} locale={i18n.language} />
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}

      {orders.data && orders.data.orders.length > 0 ? (
        <Card title={t("domains.orders")}>
          <ul className="divide-y divide-rule">
            {orders.data.orders.map((order) => (
              <OrderRow key={order.id} order={order} locale={i18n.language} format={format} />
            ))}
          </ul>
        </Card>
      ) : null}
    </section>
  );
}

/** Starts connecting a domain registered elsewhere; verification continues on its page. */
function ConnectCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const connect = useMutation({
    mutationFn: (domain: string) => api.domains.connect(domain),
    onSuccess: async (domain) => {
      await queryClient.invalidateQueries({ queryKey: ["domains", "owned"] });
      await navigate({ to: "/admin/domains/$domainId", params: { domainId: domain.id } });
    },
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const domain = name
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    if (domain) connect.mutate(domain);
  };
  return (
    <Card title={t("domains.connect.title")}>
      <p className="text-sm text-ink-2">{t("domains.connect.hint")}</p>
      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-rule bg-surface px-3 py-2 font-mono text-sm outline-none focus:border-accent"
          placeholder={t("domains.connect.placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <Button type="submit" variant="secondary" disabled={connect.isPending || !name.trim()}>
          {t("domains.connect.button")}
        </Button>
      </form>
      <ErrorNotice error={connect.error} />
    </Card>
  );
}

/** One owned domain with its auto-renew switch and an inline renewal. */
function OwnedDomainRow({
  domain: d,
  format,
}: {
  domain: OwnedDomain;
  format: (iso: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [years, setYears] = useState(1);
  const [renewing, setRenewing] = useState(false);
  const quote = useQuery({
    queryKey: ["domains", d.id, "renew-quote", years],
    queryFn: () => api.domains.renewQuote(d.id, years),
    enabled: renewing,
  });
  const toggle = useMutation({
    mutationFn: (autoRenew: boolean) => api.domains.setAutoRenew(d.id, autoRenew),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["domains", "owned"] }),
  });
  const renew = useMutation({
    mutationFn: () => api.domains.renew(d.id, years),
    onSuccess: (result) => window.location.assign(result.checkoutUrl),
  });

  return (
    <li className="flex flex-col gap-2 py-3 text-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link
            to="/admin/domains/$domainId"
            params={{ domainId: d.id }}
            className="font-mono text-accent underline-offset-2 hover:underline"
          >
            {d.name}
          </Link>
          <div className="text-xs text-ink-3">
            {t(`domains.status.${d.status}`)}
            {d.expiresAt ? ` · ${t("domains.expires", { date: format(d.expiresAt) })}` : ""}
            {d.whoisPrivacy ? ` · ${t("domains.privacy")}` : ""}
            {d.source === "external" ? ` · ${t("domains.connected")}` : ` · ${d.provider}`}
          </div>
        </div>
        {d.source === "registered" ? (
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 text-xs text-ink-2"
              title={t("domains.autoRenewHint")}
            >
              <input
                type="checkbox"
                checked={d.autoRenew}
                disabled={toggle.isPending}
                onChange={(e) => toggle.mutate(e.target.checked)}
              />
              {d.autoRenew ? t("domains.autoRenewOn") : t("domains.autoRenewOff")}
            </label>
            <Button variant="secondary" onClick={() => setRenewing((v) => !v)}>
              {t("domains.renew")}
            </Button>
          </div>
        ) : null}
      </div>
      {renewing ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md bg-surface-2 px-3 py-2">
          <div className="w-32">
            <Select value={String(years)} onChange={(e) => setYears(Number(e.target.value))}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {t("checkout.years", { count: n })}
                </option>
              ))}
            </Select>
          </div>
          {quote.data ? (
            <span className="text-xs text-ink-2">
              {formatMoney(quote.data.price.total, i18n.language)} ·{" "}
              {t("domains.newExpiry", { date: format(quote.data.newExpiresAt) })}
            </span>
          ) : (
            <span className="text-xs text-ink-3">{t("common.loading")}</span>
          )}
          <Button disabled={!quote.data || renew.isPending} onClick={() => renew.mutate()}>
            {t("domains.renewNow")}
          </Button>
          <ErrorNotice error={quote.error ?? renew.error ?? toggle.error} />
        </div>
      ) : null}
    </li>
  );
}

function DomainRow({ result, locale }: { result: DomainCheckResult; locale: string }) {
  const { t } = useTranslation();
  const status = t(
    `domains.${result.availability === "unknown" ? "unsupported" : result.availability}`,
  );
  const tone =
    result.availability === "available"
      ? "text-good"
      : result.availability === "premium"
        ? "text-accent"
        : "text-ink-3";
  const canRegister = result.registration && result.availability === "available";
  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <div className="truncate font-mono text-sm">{result.domain}</div>
        <div className={`text-xs ${tone}`}>{status}</div>
      </div>
      {canRegister && result.registration ? (
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-medium tabular-nums">
              {formatMoney(result.registration.total, locale)}
            </div>
            <div className="text-xs text-ink-3">
              {t("domains.perYear")} · {formatMoney(result.registration.wholesale, locale)} +{" "}
              {formatMoney(result.registration.icannFee, locale)} +{" "}
              {formatMoney(result.registration.processingFee, locale)}
            </div>
          </div>
          <Link
            to="/admin/domains/checkout"
            search={{ domain: result.domain, years: 1 }}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            {t("domains.register")}
          </Link>
        </div>
      ) : null}
    </li>
  );
}

function OrderRow({
  order,
  locale,
  format,
}: {
  order: Order;
  locale: string;
  format: (iso: string) => string;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between gap-4 py-3 text-sm">
      <div className="min-w-0">
        <Link
          to="/admin/orders/$orderId"
          params={{ orderId: order.id }}
          className="font-mono text-accent underline-offset-2 hover:underline"
        >
          {order.items.map((i) => i.domain).join(", ")}
        </Link>
        <div className="text-xs text-ink-3">
          {t(`order.status.${order.status}`)} ·{" "}
          {t("order.placed", { date: format(order.createdAt) })}
        </div>
      </div>
      <span className="tabular-nums">{formatMoney(order.total, locale)}</span>
    </li>
  );
}
